<script setup>
import { ref, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  faBold,
  faItalic,
  faUnderline,
  faListUl,
  faListOl,
  faQuoteRight,
  faCode,
  faMinus,
  faRotateLeft,
  faRotateRight,
  faLink,
  faUnlink,
  faTable,
} from "@fortawesome/pro-solid-svg-icons"

const props = defineProps({
  modelValue: String,
})

const emit = defineEmits(['update:modelValue'])

const editor = useEditor({
  content: props.modelValue,
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  },
  extensions: [
    StarterKit,
    Underline,
    Link.configure({
      openOnClick: false,
      defaultProtocol: 'https',
    }),
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableHeader,
    TableCell,
  ],
  
  editorProps: {
    attributes: {
      class: 'border border-gray-400 p-4 min-h-[20rem] max-h-[20rem] overflow-y-auto outline-none prose max-w-none',
    },
  },
})

function setLink() {
  const previousUrl = editor.value?.getAttributes('link').href
  const url = window.prompt('URL', previousUrl)

  if (url === null) return

  if (url === '') {
    editor.value?.chain().focus().extendMarkRange('link').unsetLink().run()
    return
  }

  editor.value?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

onBeforeUnmount(() => {
  editor.value?.destroy()
})

const tableDropdownOpen = ref(false)
function toggleTableDropdown() {
  tableDropdownOpen.value = !tableDropdownOpen.value
}
function closeTableDropdown() {
  tableDropdownOpen.value = false
}

</script>

<template>
  <div v-if="editor">
    <!-- Toolbar -->
    <section class="buttons text-gray-700 flex items-center flex-wrap gap-x-4 border-t border-l border-r border-gray-400 p-4">
      <!-- Bold -->
      <button type="button" @click="editor.chain().focus().toggleBold().run()" :class="{ 'bg-gray-200 rounded': editor.isActive('bold') }" class="p-1">
        <font-awesome-icon :icon="faBold" class="text-black" />
      </button>

      <!-- Italic -->
      <button type="button" @click="editor.chain().focus().toggleItalic().run()" :class="{ 'bg-gray-200 rounded': editor.isActive('italic') }" class="p-1">
        <font-awesome-icon :icon="faItalic" class="text-black" />
      </button>

      <!-- Underline -->
      <button type="button" @click="editor.chain().focus().toggleUnderline().run()" :class="{ 'bg-gray-200 rounded': editor.isActive('underline') }" class="p-1">
        <font-awesome-icon :icon="faUnderline" class="text-black" />
      </button>

      <!-- Headings -->
      <button type="button" @click="editor.chain().focus().toggleHeading({ level: 1 }).run()" :class="{ 'bg-gray-200 rounded': editor.isActive('heading', { level: 1 }) }" class="p-1 px-2 font-bold text-black">
        H1
      </button>
      <button type="button" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()" :class="{ 'bg-gray-200 rounded': editor.isActive('heading', { level: 2 }) }" class="p-1 px-2 font-bold text-black">
        H2
      </button>
      <button type="button" @click="editor.chain().focus().toggleHeading({ level: 3 }).run()" :class="{ 'bg-gray-200 rounded': editor.isActive('heading', { level: 3 }) }" class="p-1 px-2 font-bold text-black">
        H3
      </button>

      <!-- Link -->
      <button type="button" @click="setLink" :class="{ 'bg-gray-200 rounded': editor.isActive('link') }" class="p-1">
        <font-awesome-icon :icon="faLink" class="text-black" />
      </button>
      <button type="button" @click="editor.chain().focus().unsetLink().run()" :disabled="!editor.isActive('link')" class="p-1">
        <font-awesome-icon :icon="faUnlink" class="text-black" />
      </button>

      <!-- Lists -->
      <button type="button" @click="editor.chain().focus().toggleBulletList().run()" :class="{ 'bg-gray-200 rounded': editor.isActive('bulletList') }" class="p-1">
        <font-awesome-icon :icon="faListUl" class="text-black" />
      </button>
      <button type="button" @click="editor.chain().focus().toggleOrderedList().run()" :class="{ 'bg-gray-200 rounded': editor.isActive('orderedList') }" class="p-1">
        <font-awesome-icon :icon="faListOl" class="text-black" />
      </button>

      <!-- Quote -->
      <button type="button" @click="editor.chain().focus().toggleBlockquote().run()" :class="{ 'bg-gray-200 rounded': editor.isActive('blockquote') }" class="p-1">
        <font-awesome-icon :icon="faQuoteRight" class="text-black" />
      </button>

      <!-- Code -->
      <button type="button" @click="editor.chain().focus().toggleCode().run()" :class="{ 'bg-gray-200 rounded': editor.isActive('code') }" class="p-1">
        <font-awesome-icon :icon="faCode" class="text-black" />
      </button>

      <!-- Horizontal Rule -->
      <button type="button" @click="editor.chain().focus().setHorizontalRule().run()" class="p-1">
        <font-awesome-icon :icon="faMinus" class="text-black" />
      </button>

      <!-- Undo / Redo -->
      <button type="button" @click="editor.chain().focus().undo().run()" :disabled="!editor.can().chain().focus().undo().run()" class="p-1 disabled:text-gray-400">
        <font-awesome-icon :icon="faRotateLeft" class="text-black" />
      </button>
      <button type="button" @click="editor.chain().focus().redo().run()" :disabled="!editor.can().chain().focus().redo().run()" class="p-1 disabled:text-gray-400">
        <font-awesome-icon :icon="faRotateRight" class="text-black" />
      </button>
      
      <!-- Table icon dropdown -->
      <div class="relative">
        <button type="button" @click="toggleTableDropdown" class="p-1">
          <font-awesome-icon :icon="faTable" class="text-black" />
        </button>

        <div
          v-if="tableDropdownOpen"
          class="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
        >
          <div
            class="py-1 text-sm text-gray-700"
            @click="closeTableDropdown"
          >
            <button type="button" @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Insert Table</button>
            <button type="button" @click="editor.chain().focus().addColumnBefore().run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Add Column Before</button>
            <button type="button" @click="editor.chain().focus().addColumnAfter().run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Add Column After</button>
            <button type="button" @click="editor.chain().focus().deleteColumn().run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Delete Column</button>
            <button type="button" @click="editor.chain().focus().addRowBefore().run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Add Row Before</button>
            <button type="button" @click="editor.chain().focus().addRowAfter().run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Add Row After</button>
            <button type="button" @click="editor.chain().focus().deleteRow().run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Delete Row</button>
            <button type="button" @click="editor.chain().focus().deleteTable().run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Delete Table</button>
            <button type="button" @click="editor.chain().focus().mergeCells().run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Merge Cells</button>
            <button type="button" @click="editor.chain().focus().splitCell().run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Split Cell</button>
            <button type="button" @click="editor.chain().focus().toggleHeaderColumn().run()" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Toggle Header Column</button>
          </div>
        </div>
      </div>

    </section>

    <!-- Editor Content -->
    <div class="tiptap">

      <editor-content :editor="editor"/>
     </div>
  </div>
</template>

<style>
/* Basic editor styles */
.tiptap {
  :first-child {
    margin-top: 0;
  }

  /* Code and preformatted text styles */
  code {
    background-color: var(--purple-light);
    border-radius: 0.4rem;
    color: var(--black);
    font-size: 0.85rem;
    padding: 0.25em 0.3em;
  }

  /* Link styles */
  a {
    color: var(--purple);
    cursor: pointer;

    &:hover {
      color: var(--purple-contrast);
    }
  }

  /* Table-specific styling */
  table {
    border-collapse: collapse;
    width: 90%;
    border: 1px solid #ccc; 

    td,
    th {
      border: 1px solid #ccc; 
      padding: 8px;
      text-align: left;
      vertical-align: top;
    }

    th {
      background-color: #f0f0f0; 
      font-weight: bold;
    }

    .selectedCell:after {
      background: rgba(0, 0, 0, 0.05);
      content: "";
      position: absolute;
      top: 0; right: 0; bottom: 0; left: 0;
      z-index: 2;
      pointer-events: none;
    }

    .column-resize-handle {
      background-color: #9c27b0; 
      width: 4px;
      right: -2px;
      top: 0;
      bottom: 0;
      position: absolute;
      pointer-events: none;
    }
  }

  .tableWrapper {
    margin: 1.5rem 0;
    overflow-x: auto;
  }

  &.resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }
}
</style>
